import { EntitiesResponse } from "@api/api.types";
import { getFormattedCashBackValue, getTitle } from "@api/mappers/shared";

import { DeliveryTypes, ProductVisibilityTypes } from "@utils/enums";
import { getMedias } from "@utils/medias";

import { ProductPromotionAlgoliaModel } from "lib/@types/service/product/algolia/productPromotion.algolia.model";
import { FullProductApiModel } from "lib/@types/service/product/api/product.api.model";

import { ArticleConfiguration } from "../../@types/service/cart/shared";
import { ProductAlgoliaModel } from "../../@types/service/product/algolia/product.algolia.model";
import { ProductFeatureAlgoliaModel } from "../../@types/service/product/algolia/productFeature.algolia.model";
import { ProductTileApiModel } from "../../@types/service/product/api/productTile.api.model";
import {
    Brand,
    FeatureKind,
    ProductAnalyticsModel,
    ProductPromotion,
    SalesIncentive,
} from "../../@types/service/product/shared";
import { Media, PromoLabel } from "../../@types/service/shared";
import { ModelMapper } from "./mappers.types";
import { getPromoLabel } from "./promoLabel.mapper";

export const ProductsMapper =
    <TIn, TOut>(
        mapper: ModelMapper<TIn, TOut>,
    ): ModelMapper<EntitiesResponse<TIn>, Array<TOut>> =>
    model => {
        return model.entities.map(mapper);
    };

// ProductApiModel -> ProductTileModel
export const ProductTileMapper: ModelMapper<
    ProductTileApiModel,
    ProductTileModel
> = model => {
    return {
        sku: model.sku,
        ean: model.ean,
        url: model.articleConfiguration ? undefined : model.url,
        disablePDPLink: !!model.disablePDPLink,
        title: getTitle(model),
        ecochecksAcceptedAsPayment: model.ecochecksAcceptedAsPayment,
        productVisibilityType:
            model.productVisibilityType as ProductVisibilityTypes,
        deliveryTypes: model.deliveryTypes || [],
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        articleConfiguration: model.articleConfiguration,
        formattedCashBackValue: getFormattedCashBackValue(
            model.price?.promotions || [],
        ),
        salesIncentives: (model.salesIncentives || []).sort(
            (a: { order: number }, b: { order: number }) =>
                a.order > b.order ? 1 : -1,
        ),
        discountPercentage: model.price?.discountPercentage,
        medias: getMedias(
            false,
            getTitle(model),
            model.mainImages,
            model.articleConfiguration,
        ),
        showStrippedPrice: !!model.price?.strikethroughPrice,
        strikethroughPrice: model.price?.strikethroughPrice || null,
        showConvertedPriceAsMainPrice: model.showConvertedPriceAsMainPrice,
        conversionPrice: model.price?.conversionPrice || null,
        conversionUnit: model.price?.conversionUnit || null,
        currentConversionPrice: model.price?.currentConversionPrice || null,
        salesPrice: model.price?.currentPrice || null,
        features: model.features,
        superCategoryName: model.superCategoryName,
        superCategoryUrl: model.superCategoryUrl,
        analytics: model.analytics,
        brand: model.brand,
        isAdditionalService: (model.montageParents || []).length > 0,
        promotions: model.price?.promotions ?? [],
        boxArticle: model.boxArticle,
        promoLabel: model.promoLabel,
    };
};

export const FullProductTileMapper: ModelMapper<
    FullProductApiModel,
    ProductTileModel
> = model => {
    return {
        sku: model.sku,
        ean: model.ean,
        url: model.articleConfiguration ? undefined : model.url,
        disablePDPLink: !!model.disablePDPLink,
        title: getTitle(model),
        ecochecksAcceptedAsPayment: model.ecochequesAccepted,
        productVisibilityType:
            model.productVisibilityType as ProductVisibilityTypes,
        deliveryTypes: model.deliveryTypes || [],
        minimumOrderQuantity: model.minimumOrderQuantity || 1,
        articleConfiguration: model.articleConfiguration,
        formattedCashBackValue: getFormattedCashBackValue(
            model.price?.promotions || [],
        ),
        salesIncentives: (model.salesIncentives || []).sort(
            (a: { order: number }, b: { order: number }) =>
                a.order > b.order ? 1 : -1,
        ),
        discountPercentage: model.price?.discountPercentage,
        medias: getMedias(
            false,
            getTitle(model),
            model.mainImages,
            model.articleConfiguration,
        ),
        showStrippedPrice: !!model.price?.strikethroughPrice,
        strikethroughPrice: model.price?.strikethroughPrice || null,
        showConvertedPriceAsMainPrice: model.showConvertedPriceAsMainPrice,
        conversionPrice: model.price?.conversionPrice || null,
        conversionUnit: model.price?.conversionUnit || null,
        currentConversionPrice: model.price?.currentConversionPrice || null,
        salesPrice: model.price?.currentPrice || null,
        features: model.features,
        superCategoryName: model.superCategoryName,
        superCategoryUrl: model.superCategoryUrl,
        analytics: model.analytics,
        brand: model.brand,
        isAdditionalService: model.montageParents.length > 0,
        promotions: model.price?.promotions ?? [],
        boxArticle: model.boxArticle,
        promoLabel: model.promoLabel,
    };
};

export type ProductTileModel = {
    sku: string;
    ean: string;
    productVisibilityType: ProductVisibilityTypes;
    disablePDPLink: boolean;
    deliveryTypes: DeliveryTypes[];
    minimumOrderQuantity: number;
    url?: string;
    title: string;
    articleConfiguration?: ArticleConfiguration | null;
    ecochecksAcceptedAsPayment: boolean;
    formattedCashBackValue?: string;
    discountPercentage?: number | null;
    salesIncentives: Array<SalesIncentive>;
    medias: Array<Media> | null;
    showStrippedPrice: boolean;
    showConvertedPriceAsMainPrice: boolean;
    strikethroughPrice?: number | null;
    conversionPrice?: number | null;
    conversionUnit?: string | null;
    currentConversionPrice?: number | null;
    salesPrice?: number | null;
    features: Array<ProductFeatureModel>;
    superCategoryUrl: string;
    superCategoryName: string;
    brand?: Brand | null;
    analytics: ProductAnalyticsModel;
    isAdditionalService: boolean;
    promotions: Array<ProductPromotion>;
    boxArticle: boolean;
    promoLabel?: PromoLabel;
};

export type ProductFeatureModel = {
    name: string;
    code?: string;
    unit?: {
        code: string;
        name: string;
        symbol: string;
    } | null;
    toolTipImage?: Media | null;
    featureValues: Array<FeatureValueModel>;
    coreFeature: boolean;
    kind?: FeatureKind;
};

export type FeatureValueModel = {
    formattedValue: string;
};

// ProductAlgoliaModel -> ProductTileModel
export const AlgoliaModelMapper = (
    algoliaModel: ProductAlgoliaModel,
): ProductTileModel => {
    return {
        ean: algoliaModel.ean,
        sku: algoliaModel.sku,
        brand: { name: algoliaModel.brand || "" },
        disablePDPLink: algoliaModel.disablePDPLink,
        ecochecksAcceptedAsPayment: algoliaModel.ecochecksAcceptedAsPayment,
        title: algoliaModel.productTitle || algoliaModel.name || "",
        productVisibilityType:
            algoliaModel.productVisibilityType as ProductVisibilityTypes,
        url: algoliaModel.url,
        deliveryTypes: algoliaModel.deliveryTypes as DeliveryTypes[],
        minimumOrderQuantity: algoliaModel.minimumOrderQuantity || 1,
        medias: AlgoliaMediaMapper(algoliaModel) || null,
        features: algoliaModel.features.map(AlgoliaFeatureMapper),
        superCategoryName: algoliaModel.categoryName || "",
        superCategoryUrl: algoliaModel.categoryUrl,
        showConvertedPriceAsMainPrice:
            algoliaModel.showConvertedPriceAsMainPrice,
        salesIncentives: algoliaModel.salesIncentives,
        conversionPrice: algoliaModel.price.conversionPrice,
        conversionUnit: algoliaModel.price.conversionUnit,
        currentConversionPrice: algoliaModel.price.currentConversionPrice,
        strikethroughPrice: algoliaModel.price.strikethroughPrice,
        salesPrice: algoliaModel.price.currentPrice,
        showStrippedPrice: !!algoliaModel.price.strikethroughPrice,
        discountPercentage: algoliaModel.price.discountPercentage,
        formattedCashBackValue: getFormattedCashBackValue(
            algoliaModel.price.promotions,
        ),
        promotions:
            algoliaModel.price?.promotions.map(AlgoliaPromotionMapper) ?? [],
        analytics: {
            productTitle: algoliaModel.productTitle || algoliaModel.name || "",
            superCategoryCode: algoliaModel.categoryName || "",
            superCategoryName: algoliaModel.categoryName?.toLowerCase() || "",
        },
        // Additional services aren't exported to Algolia so this product cannot be an additional service
        isAdditionalService: false,
        boxArticle: algoliaModel.boxArticle,
        promoLabel:
            algoliaModel.promoLabel ||
            getPromoLabel(
                algoliaModel.salesIncentives,
                algoliaModel.price.discountPercentage,
                algoliaModel.price.promotions,
            ),
    };
};

const AlgoliaPromotionMapper = (
    algoliaPromotionModel: ProductPromotionAlgoliaModel,
) => ({
    promotionPercent: algoliaPromotionModel.promotionPercentage,
    promotionType: algoliaPromotionModel.promotionType,
    promotionValue: algoliaPromotionModel.promotionValue,
    validTo: algoliaPromotionModel.validTo,
    validFrom: algoliaPromotionModel.validFrom,
    periodText: algoliaPromotionModel.periodText,
});

const AlgoliaMediaMapper = (algoliaModel: ProductAlgoliaModel) => {
    const title = algoliaModel.productTitle || algoliaModel.name || "";

    return getMedias(false, undefined, algoliaModel.mainImages)?.map<Media>(
        media => ({
            ...media,
            altText: title,
            description: title,
        }),
    );
};

const AlgoliaFeatureMapper = (algoliaFeature: ProductFeatureAlgoliaModel) => {
    const feature: ProductFeatureModel = {
        name: algoliaFeature.name,
        code: algoliaFeature.code,
        kind: {
            name: algoliaFeature.name,
            code: algoliaFeature.code,
            sortOrder: algoliaFeature.ranking,
        },
        featureValues: algoliaFeature.values.map(algoliaFeatureValue => ({
            formattedValue: algoliaFeatureValue,
        })),
        coreFeature: false,
        unit: {
            name: algoliaFeature.unit,
            code: algoliaFeature.unit,
            symbol: algoliaFeature.unit,
        },
    };

    return feature;
};
